<template>
	<div v-if="showInitialWidget" class="ContractorDashboardWidget">
		<div class="contractor-dashboard-widget-container">
			<span class="container-title">{{ $t('text.contractorDashboardTitle', { username: $store.state.loggedInUser?.fields.name.de }) }}</span>
			
			<!-- Contract Insights Section -->
			<template v-if="contractorHasContractsThatNeedAttention">
				<div class="info-container container-inner">
					<div class="inner-title">
						<v-icon size="20px">mdi-gavel</v-icon><span>{{ $t('text.contractInsights') }}</span>
					</div>
					<!-- Terminated Contracts Section -->
					<div v-if="terminatedContracts.length" class="contracts-grid" :class="{ 'single-contract': terminatedContracts.length === 1 }">
						<ContractCardTiny 
							v-for="contract in terminatedContracts" 
							:key="contract.id" 
							:contract="contract"
							:client-name="clientNames[contract.contractTemplate?.contractSequence?.clientId]"
						/>
					</div>

					<!-- Active Contracts Section -->
					<div v-if="activeContracts.length" class="contracts-grid" :class="{ 'single-contract': activeContracts.length === 1 }">
						<ContractCardTiny 
							v-for="contract in activeContracts" 
							:key="contract.id" 
							:contract="contract"
							:client-name="clientNames[contract.contractTemplate?.contractSequence?.clientId]"
						/>
					</div>
					<div class="inner-info" v-html="$t('text.contractsDashboardInfo')"/>
					<div class="inner-button">
						<v-btn class="blueButton" @click="$router.push('/contractSigning')">{{ $t('text.viewContracts') }}</v-btn>
					</div>
				</div>
			</template>
			
			<!-- Error Container for Missing Contractor Details -->
			<template v-else-if="!hasTourOperatorContracts && contractor && hasMissingFields">
				<div class="error-container container-inner">
					<div class="inner-title">
						<v-icon size="24px">mdi-alert</v-icon>
						<span>{{ $t('text.completeContractDetails') }}</span>
					</div>
					<span class="inner-info" v-html="$t('text.completeContractDetailsText', { marketplace: currentClientTitle })" />
					<span class="inner-link" @click="openContractorDialog()">{{ $t('text.completeData') }}</span>
				</div>
			</template>

			<!-- Pending Approval Warning -->
			<template v-else-if="!hasTourOperatorContracts && isInPendingState && !isExistingServiceProvider">
				<div class="warning-container container-inner">
					<div class="inner-title">
						<v-icon size="24px">mdi-clock-outline</v-icon>
						<span>{{ $t('text.pendingApproval') }}</span>
					</div>
					<span class="inner-info" v-html="$t('text.pendingApprovalDetailsText', { marketplace: currentClientTitle })" />
				</div>
			</template>

			<!-- Getting Started Section - Only for New Service Providers -->
			<div v-if="!isExistingServiceProvider && showGettingStarted" class="container-inner">
				<div class="inner-list">
					<div class="inner-list-title">
						<v-icon size="20px">mdi-rocket-launch</v-icon> 
						<span>{{ $t('text.gettingStarted') }}</span>
					</div>
					<div v-if="!hasTourOperatorContracts" class="inner-list-item" :class="{ completed: isBusinessProfileValid }">
						<span class="sp-circle"><v-icon size="16px">mdi-check</v-icon></span>
						<div>
							<span>{{ $t('text.setUpYourBusinessProfile') }}</span>
							<span>{{ $t('text.setUpBusinessProfileInfoText') }}</span>
						</div>
						<a href="/businessProfile">{{ $t('text.setUpBusinessProfile') }}</a>
					</div>
					<div v-if="!hasTourOperatorContracts" class="inner-list-item" :class="{ completed: hasServices }">
						<span class="sp-circle"><v-icon size="16px">mdi-check</v-icon></span>
						<div>
							<span>{{ $t('text.createYourServices') }}</span>
							<span>{{ $t('text.createYourServicesInfoText') }}</span>
						</div>
						<a href="/services">{{ $t('text.designYourServices') }}</a>
					</div>
					<div class="inner-list-item" :class="{ completed: !hasMissingFields, disabled: !contractor }">
						<span v-if="!hasTourOperatorContracts || !hasMissingFields" class="sp-circle"><v-icon size="16px">mdi-check</v-icon></span>
						<span v-else class="to-circle">1</span>
						<div>
							<span>{{ $t('text.completeContractDetails') }}</span>
							<span>{{ $t(hasTourOperatorContracts ? 'text.completeContractDetailsInfoTextTourOperator' : 'text.completeContractDetailsInfoText') }}</span>
						</div>
						<a @click="openContractorDialog()" style="cursor: pointer;">{{ $t('text.completeData') }}</a>
					</div>
					<div v-if="hasTourOperatorContracts" class="inner-list-item" :class="{ completed: !contractorHasContractsThatNeedAttention }">
						<span v-if="!contractorHasContractsThatNeedAttention" class="sp-circle"><v-icon size="16px">mdi-check</v-icon></span>
						<span v-else class="to-circle">2</span>
						<div>
							<span>{{ $t('text.signTourOperatorAgreement') }}</span>
							<span>{{ $t('text.signTourOperatorAgreementInfoText') }}</span>
						</div>
						<a href="/contractSigning">{{ $t('text.viewContracts') }}</a>
					</div>
					<div v-if="hasTourOperatorContracts" class="inner-list-item" :class="{ completed: tradeHubAppVisited, disabled: !hasSignedTourOperatorContract }">
						<span v-if="tradeHubAppVisited && hasSignedTourOperatorContract" class="sp-circle"><v-icon size="16px">mdi-check</v-icon></span>
						<span v-else class="to-circle">3</span>
						<div>
							<span>{{ $t('text.accessTheTradeHub') }}</span>
							<span>{{ $t('text.accessTheTradeHubInfoText') }}</span>
						</div>
						<a :href="tradeHubLink" @click="markTradeHubVisited(true)">{{ $t('text.exploreTradeHub') }}</a>
					</div>
				</div>
			</div>
		</div>
		<StepperDialog ref="contractorDialog" v-model="contractorClone" v-model:stepId="step" group="Contractor" :onConfirm="updateContractor" :confirm-label="$t('text.completeRegistration')"
			:order="[ steps ]" :disableConfirmButton="step === 'businessInformation' ? hasFormOneError : hasFormTwoError" :height="'calc(100vh - 5px)'" class="ContractStepperDialog" @close="resetContractErrors"
		>
			<template #title>
				<v-toolbar-title><span>{{ $t("text.contractorData") }}</span></v-toolbar-title>
			</template>
			<Step id="businessInformation" group="Contractor" icon="mdi-briefcase-variant" class="StepWithoutStepper" :force-status="hasFormOneError ? 'error' : undefined">
				<ContractorForm v-if="step === 'businessInformation'" v-model="contractorClone" :step="step" :isTourOperator="hasTourOperatorContracts" @update:modelValue="contractorClone = $event" @update:hasFormError="hasFormOneError = $event" />
			</Step>
			<Step id="contractingInformation" group="Contractor" icon="mdi-gavel" class="StepWithoutStepper" :disabled="hasFormOneError" :force-status="hasFormOneError || !hasVisitedContractingInformation ? 'incomplete' : (hasFormTwoError ? 'error' : undefined)">
				<ContractorForm v-if="step === 'contractingInformation'" v-model="contractorClone" :step="step" :isTourOperator="hasTourOperatorContracts" @update:modelValue="contractorClone = $event" @update:hasFormError="hasFormTwoError = $event" />
			</Step>
		</StepperDialog>
	</div>
</template>
<script lang="ts">
import Common from '../../mixins/Common.vue'
import ContractExecutive from '../../../../api2/src/modules/contract/ContractExecutive'
import StepperDialog from '../../components/common/StepperDialog.vue'
import ContractorForm from '../../components/contract/ContractorForm.vue'
import { metadata } from '../../../../api2/src/framework/model-util'
import ContractCardTiny from '../../components/contract/ContractCardTiny.vue'
import Loading from '../../mixins/Loading.vue'
import Step from '../../views/applications/packageDesigner/Step.vue';
import { Contract, Contractor, ContractStatus } from '../../../../api2/src/modules/contract/contract.entities'
import { ContractSequenceType } from '../../../../shared/types/account-types'
import { NotFoundError } from '../../../../api2/src/framework/rmi-errors'

export default {
	name: 'ContractorDashboardWidget',
	components: { ContractorForm, ContractCardTiny, StepperDialog, Step },
	mixins: [ Common, Loading ],
	data: () => ({
		contractor: null as Contractor | null,
		contractorClone: null,
		hasFormOneError: false,
		hasFormTwoError: false,
		hasVisitedContractingInformation: false,
		contractorHasContractsThatNeedAttention: false,
		contractsThatNeedAttention: [] as Contract[],
		contractorContractTypes: [] as ContractSequenceType[],
		clientNames: {},
		isBusinessProfileValid: false,
		hasServices: false,
		step: 'businessInformation',
		steps: ['businessInformation', 'contractingInformation'],
		contractsByContractor: {} as Record<string, Contract[]>,
		hasActiveOrExpiredServices: false,
		isLoading: true
	}),
	computed: {
		tradeHubAppVisited() {
			const userId = this.$store.state.loggedInUser?.sys?.id
			const serviceProviderId = this.serviceProvider?.sys?.id
			if (!userId || !serviceProviderId) {
				return false
			}
			const key = `${userId}:${serviceProviderId}`
			return this.$store.state.tradeHubVisitedByUserAndServiceProvider?.[key] === true
		},
		hasSignedTourOperatorContract() {
			const contractorId = this.contractor?.id
			if (!contractorId) {
				return false
			}
			const contracts = this.contractsByContractor?.[contractorId] ?? []
			return contracts.some(contract =>
				contract?.status === ContractStatus.SIGNED &&
				contract?.contractTemplate?.contractSequence?.type === ContractSequenceType.TOUR_OPERATOR
			)
		},
		tradeHubLink() {
			return '/app/trade-hub'
		},
		showGettingStarted() {
			return !this.isLoading && ((!this.hasTourOperatorContracts && (!this.isBusinessProfileValid || !this.hasServices)) || this.hasMissingFields || (this.hasTourOperatorContracts && (!this.tradeHubAppVisited || this.contractorHasContractsThatNeedAttention)))
		},
		serviceProvider() {
			return this.$store.state.isImpersonation ? this.$store.state.selectedServiceProvider : this.$store.state.loggedInUser?.fields?.serviceProvider?.de
		},
		clientAssignment() {
			return this.serviceProvider?.fields?.clientAssignments?.de?.find(x => x.fields.client.de.sys.id === this.$store.state.selectedClient.sys.id)
		},
		loggedInEmail() {
			if (!this.userIsServiceProvider) {
				return null
			}
			return this.serviceProvider?.fields?.userAccount?.de?.fields?.email?.de
		},
		hasMissingFields() {
			const contractorFields = Object.entries(metadata.Contractor.fields).filter(([_, value]) => value.required).map(([key]) => key)
			const businessProfileData = this.contractor?.businessProfileData
			let businessProfileDataFields = Object.entries(metadata.BusinessProfileData.fields)
				.filter(([_, value]) => value.id !== 'contractorEmail' && value.required)
				.map(([key]) => key)

			if (businessProfileData?.dontHaveIban) {
				businessProfileDataFields = businessProfileDataFields.filter(field => !['accountHolder', 'iban', 'bic', 'creditInstitution'].includes(field))
			}

			return !this.contractor
				|| !businessProfileData
				|| contractorFields.some(x => !this.contractor[x])
				|| businessProfileDataFields.some(x => !businessProfileData[x])
				|| (!businessProfileData.dontHaveUidNumber && !businessProfileData.uidNumber)
		},
		isInPendingState() {
			return this.clientAssignment?.fields?.status?.de === 'pending'
		},
		showInitialWidget() {
			return !this.isLoading && (this.userIsServiceProvider && (this.showGettingStarted || (!this.hasTourOperatorContracts && this.isInPendingState) || this.contractorHasContractsThatNeedAttention))
		},
		currentClientTitle() {
			return this.$store.state.selectedClient.fields.clientId?.de
		},
		terminatedContracts() {
			return this.contractsThatNeedAttention.filter(contract => 
				this.isTerminated(contract)
			) as Contract[]
		},
		activeContracts() {
			return this.contractsThatNeedAttention.filter(contract => 
				!this.isTerminated(contract)
			) as Contract[]
		},
		isExistingServiceProvider() {
			return this.clientAssignment?.fields?.status?.de === 'approved' && 
				   this.hasActiveOrExpiredServices
		},
		hasTourOperatorContracts() {
			return this.contractorContractTypes.some(type => type === ContractSequenceType.TOUR_OPERATOR)
		},
		hasServiceProviderContracts() {
			return this.contractorContractTypes.some(type => type === ContractSequenceType.SERVICE_PROVIDER)
		},
	},
	watch: {
		showInitialWidget: {
			handler() {
				this.fetchContractsThatNeedAttention()
			},
		},
		isLoading(value) {
			if (value === false) {
				this.syncTradeHubVisited()
			}
		},
		hasSignedTourOperatorContract() {
			if (!this.isLoading) {
				this.syncTradeHubVisited()
			}
		},
		step(value) {
			if (value === 'contractingInformation') {
				this.hasVisitedContractingInformation = true
			}
		},
	},
	methods: {
		resetContractErrors() {
			this.hasFormOneError = false
			this.hasFormTwoError = false
			this.hasVisitedContractingInformation = false
		},
		isTerminated(contract: Contract) {
			return contract.status === ContractStatus.TERMINATED || !!contract.endDate
		},
		openContractorDialog() {
			this.contractorClone = JSON.parse(JSON.stringify(this.contractor))
			this.step = 'businessInformation'
			this.hasVisitedContractingInformation = false
			if (this.$refs.contractorDialog) {
				this.$refs.contractorDialog.open()
			}
		},
		async getContractsAndContractors() {
			let contractExecutive = new ContractExecutive(this)
			if (!this.loggedInEmail) {
				this.showError('Not Authorized!')
// TODO: what was the intention of this?
//       i had to remove it because this triggers login a second time
//				this.showError('Not Authorized!')
//				setTimeout(() => {
//					this.$emit('show-login')
//				}, 1000)
				return
			}
			const clientId = this.$store.state.selectedClient.sys.id
			const providerId = this.$store.state.selectedServiceProvider.sys.id
			let result
			try {
				result = await contractExecutive.getContractsAndContractorsByProviderId(providerId)
			}
			catch (error) {
				if (error instanceof NotFoundError) {
					this.showError(error.message, false)
				}
				this.contractor = null
				this.contractsByContractor = {}
				this.clientNames = {}
				return
			}

			this.contractor = result.contractors.find(contractor => contractor.clientId === clientId) || null
			this.contractsByContractor = result.contractsByContractor
			this.clientNames = result.clientNames
		},
		async updateContractor() {
			if (this.step === 'businessInformation') {
				return true
			}
			await this.loadingOverlay(async () => {
				try {
					let contractExecutive = new ContractExecutive(this)
					await contractExecutive.bulkUpdateContractors([this.contractorClone])
					await this.getContractsAndContractors()
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'updateContractor')
		},
		async fetchContractsThatNeedAttention() {
			if (!this.contractor) {
				return
			}

			await this.loadingOverlay(async () => {
				try {
					let contractExecutive = new ContractExecutive(this)
					const result = await contractExecutive.getContractsThatNeedAttention(this.contractor.id)
					// show terminated contracts first (based on designs)
					this.contractsThatNeedAttention = result
					this.contractorHasContractsThatNeedAttention = result.length > 0
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'fetchContractsThatNeedAttention')
		},
		async checkServiceProviderFields() {
			await this.loadingOverlay(async () => {
				try {
					const result = await this.$httpGet(`/check-missing-data?serviceProviderId=${this.serviceProvider.sys.id}&clientId=${this.$store.state.selectedClient.sys.id}&serviceLocale=${this.serviceLocale}`)
					this.isBusinessProfileValid = result.status === 'complete'
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'checkServiceProviderFields')
		},
		async getProducts() {
			await this.loadingOverlay(async () => {
				try {
					const statuses = ['active', 'active_saved', 'pending', 'deactivated', 'archived', 'expired', 'saved']
					const result = await this.$httpGet(`/product-count?serviceProviderId=${this.serviceProvider.sys.id}&client=${this.$store.state.selectedClient.sys.id}&status=${statuses}`)
					this.hasServices = result.count > 0
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'getProducts')
		},
		async getActiveOrExpiredServices() {
			await this.loadingOverlay(async () => {
				try {
					const statuses = ['active', 'expired']
					const result = await this.$httpGet(`/product-count?serviceProviderId=${this.serviceProvider.sys.id}&client=${this.$store.state.selectedClient.sys.id}&status=${statuses}`)
					this.hasActiveOrExpiredServices = result.count > 0
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'getActiveOrExpiredServices')
		},
		async getContractorContractTypes() {
			if (!this.contractor) {
				return
			}

			await this.loadingOverlay(async () => {
				try {
					let contractExecutive = new ContractExecutive(this)
					const result = await contractExecutive.getContractorContractTypes(this.contractor.id)
					this.contractorContractTypes = result
				}
				catch (error) {
					this.showError(error.message)
				}
			}, 'getContractorContractTypes')
		},
		async initializeData() {
			try {
				this.isLoading = true
				
				await Promise.all([
					this.getContractsAndContractors(),
					this.getProducts(),
					this.checkServiceProviderFields(),
					this.getActiveOrExpiredServices(),
				])

				// Keep the widget hidden until contractor-dependent checks are resolved as well.
				await Promise.all([
					this.getContractorContractTypes(),
					this.fetchContractsThatNeedAttention(),
				])
			}
			catch (error) {
				console.error('Error initializing data', error)
			}
			finally {
				this.isLoading = false
			}
		},
		syncTradeHubVisited() {
			if (this.hasSignedTourOperatorContract) {
				return
			}
			this.markTradeHubVisited(false)
		},
		markTradeHubVisited(visited) {
			const userId = this.$store.state.loggedInUser?.sys?.id
			const serviceProviderId = this.serviceProvider?.sys?.id
			if (!userId || !serviceProviderId) {
				return
			}
			this.$store.commit('setTradeHubVisitedForServiceProvider', { userId, serviceProviderId, visited })
		},
	},
	async created() {
		await this.initializeData()
	},
}
</script>
<style lang="scss" scoped>
.ContractorDashboardWidget { padding: 20px 48px 12px 48px; }
.contractor-dashboard-widget-container { 
	background-color: white; 
	padding: 32px 64px; 
	border-radius: 4px; 
	display: flex;
	flex-direction: column;
	gap: 32px;
	.container-title {
		font-size: 20px;
		font-weight: 600;
		line-height: 25px;
		letter-spacing: -0.196px;
	}
	.container-inner {		
		border-radius: 4px;
		display: flex;
    	flex-direction: column;

		.inner-title {
			display: flex;
			margin-bottom: 16px;
		}

		&.warning-container,
		&.error-container {
			border: 1px solid #DBDBDB;
			padding: 24px 24px 24px 12px;
			> .inner-title {
				font-size: 17px;
				font-weight: 500;
				line-height: 22px;
				letter-spacing: -0.071px;
				gap: 6px;
			}
			> .inner-info :deep(a) {
					color: #00AEEF;
					&:link,
					&:visited,
					&:hover,
					&:active {
						color: #00AEEF;
					}
				}
		}
		&.error-container {
			border-left: 6px solid #F34545;
			.inner-title {
				color: #F34545;
			}
		}
		&.warning-container {
			border-left: 6px solid #FFB400;
			.inner-title {
				color: #FFB400;
			}
		}
		&.info-container {
			position: relative;
			> .inner-title {
				font-size: 15px;
				font-weight: 500;
				line-height: 20px;
				letter-spacing: -0.037px;
				gap: 16px;
			}
		}
		.inner-info {
			font-size: 15px;
			font-weight: 400;
			line-height: 20px;
			letter-spacing: -0.037px;
		}
		.inner-link {
			font-size: 15px;
			font-weight: 600;
			line-height: 20px;
			letter-spacing: -0.037px;
			color: #00AEEF;
			cursor: pointer;
			padding-top: 4px;
		}
		.inner-list {
			> .inner-list-title {
				padding-bottom: 16px;
				display: flex;
				align-items: center;
				gap: 16px;
				> span {
					font-size: 17px;
					font-weight: 600;
					line-height: 22px;
					letter-spacing: -0.071px;
				}
			} 
			> .inner-list-item {
				height: 70px;
				display: flex;
				align-items: center;
				border-bottom: 1px solid rgba(0, 0, 0, 0.10);
				&:last-of-type {
					border-bottom: 0px;
				}
				.to-circle {
					width: 24px;
					height: 24px;
					border-radius: 100px;
					background: #DBDBDB;
					color: #424242;
					text-align: center;
					font-size: 12px;
					font-weight: 600;
					line-height: 16px;
					letter-spacing: 0.001px;
					display: flex;
					justify-content: center;
					align-items: center;
				}
				.sp-circle {
					width: 24px;
					height: 24px;
					display: flex;
					justify-content: center;
					align-items: center;
					border-radius: 100px;
					background: #DBDBDB;
					color: #8F8F8F;
				}
				> div {
					padding-left: 16px;
					display: flex;
					flex-direction: column;
					flex-grow: 1;
					> span:first-child {
						font-size: 17px;
						font-weight: 600;
						line-height: 22px;
						letter-spacing: -0.071px;
					}
					> span:last-child {
						font-size: 15px;
						font-weight: 400;
						line-height: 20px;
						letter-spacing: -0.037px;
					}
				}

				> a {
					color: #00AEEF;
					font-size: 15px;
					font-weight: 500;
					line-height: 20px;
					letter-spacing: -0.037px;
					&:link,
					&:visited,
					&:hover,
					&:active {
						color: #00AEEF;
					}
				}

				&.completed {
					.sp-circle {
						background: #CBEDB6;
						color: #3C7815;
					}
				}
				&.completed,
				&.disabled {
					> a {
						pointer-events: none;
						color: #8F8F8F;
					}
				}
				&.disabled {
					pointer-events: none;
					opacity: 0.3;
				}
			}
		}
	}
}
.contracts-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 16px;
	margin-bottom: 24px;

	&.single-contract {
		grid-template-columns: 1fr;
	}
}

.inner-button {
	display: flex;
	justify-content: flex-start;
	margin-top: 24px;
}
</style>

<style lang="scss">
.ContractStepperDialog .StepWithoutStepper {
	margin-top: 0px;
	position: relative;
}
</style>
